/* V164.1 — Single source of truth for admin tab descriptions.
 *
 * Each entry: { description, useWhen, badge?, popular? }
 *   description — 1-line "what it does"
 *   useWhen     — micro-copy "when you actually need it"
 *   badge       — optional UI badge: NEW | HOT | BETA
 *   popular     — surfaces it in the "💡 Popular" recommendations row
 *
 * Add new entries here when you ship a new admin tab. The grouped-tabs
 * navigation component reads this map to render tooltips + descriptions.
 */
export const ADMIN_TAB_META = {
  // ---- Overview ----
  leads: {
    description: "Zoznam všetkých leadov ktorí prešli funnel formulárom.",
    useWhen: "Keď chceš ručne kontaktovať toho čo nezaplatil.",
    popular: true,
  },
  chats: {
    description: "Logy z AI chatbota — celá konverzácia + grade.",
    useWhen: "Keď chceš pozrieť ako chatbot odpovedá v reálnych situáciách.",
  },
  analytics: {
    description: "Konverzný funnel: scan → email → upgrade.",
    useWhen: "Týždenný check: kde nám padá konverzia?",
    popular: true,
  },
  notifs: { description: "Push notifikácie + email broadcasty.", useWhen: "Pri spustení novej fičúry." },
  url_analytics: { description: "Každá URL: zobrazenia, kliky na objednávku a konverzia.", useWhen: "Keď chceš vedieť, ktorá stránka predáva najlepšie.", badge: "NEW", popular: true },
  funnel_summary: { description: "Všetky funnely: návštevníci, objednávky, tržby, cena reklamy, ROAS a zisk za mesiac.", useWhen: "Keď chceš vedieť, ktorá reklama zarába.", badge: "NEW", popular: true },
  funnel_campaigns: { description: "Funnely pre firmy, Gratulujeme a Tvorba webu €499 s obrázkami a textami pre FB, IG a Google + výsledky.", useWhen: "Pri spúšťaní reklamných kampaní.", badge: "NEW", popular: true },
  persona_ads: { description: "6 hotových reklám (obava / analytik / bežný človek) s výsledkami ich stránok.", useWhen: "Pri spúšťaní Meta kampane a porovnaní, ktorá verzia vyhráva.", badge: "NEW" },
  waitlist: { description: "Coming-soon agenti waitlist + demand votes.", useWhen: "Pri rozhodovaní čo postaviť ďalšie." },
  affiliates: { description: "Referral programy + payouts.", useWhen: "Pri vyplácaní mesačnej komisie." },
  security: { description: "Failed login attempts, brute force lockouts, audit log.", useWhen: "Keď máš podozrenie na hack." },

  // ---- Marketing (top priority for revenue) ----
  launch_checklist: {
    description: "🚀 8-bodový audit: si ready spustiť FB Ads? + budget recommendation.",
    useWhen: "PRED každou kampaňou. Jediná obrazovka ti povie OK / NOT OK.",
    badge: "NEW",
    popular: true,
  },
  ad_creative_studio: {
    description: "🎨 Canva-like generátor PNG reklám (handwritten / vs / before-after).",
    useWhen: "Keď potrebuješ statické creative pre Meta Ads za 60 sekúnd.",
    badge: "NEW",
    popular: true,
  },
  ad_studio: {
    description: "🎬 Animované 44s video reklamy v 6 jazykoch × 3 aspect (batch record).",
    useWhen: "Keď chceš video pre IG Reels / TikTok / YouTube Shorts.",
    popular: true,
  },
  discounts: { description: "Zľavové kódy (vytvoriť, zrušiť, sledovať usage).", useWhen: "Pri sezónnej kampani alebo blokovaní zneužitia." },
  emails: { description: "Email broadcast history + open/click rates (Resend).", useWhen: "Po každej kampani: kto otvoril, kto klikol." },
  ab_stats: { description: "A/B test stats pre lifecycle emaily.", useWhen: "Optimalizácia subject linov a copy." },
  drip_ab: { description: "Multi-variant drip email pipeline (5 stages × 6 jazykov).", useWhen: "Pri tuningu post-scan funnelu." },
  cinematic_ads: { description: "AI-generované video creative cez Gemini Nano Banana + GPT-Image.", useWhen: "Experimentálne — pre brand storytelling." },
  visionary_copy: { description: "LLM copy variants per audience segment.", useWhen: "Keď potrebuješ rýchlo 10 variantov headline." },
  seo_indexing: { description: "Google Search Console submissions + sitemap auto-ping.", useWhen: "Po publishe nového blog článku." },
  ads: { description: "A/B test live ads ktoré rotujú v UI banneroch.", useWhen: "Tuning header CTA / footer banner." },
  fbig_ads: { description: "FB + IG targeting strategy + audience builder.", useWhen: "Pri tvorbe novej Custom Audience." },
  fb_ads_library: { description: "Curated FB ad templates pre rýchle skopírovanie.", useWhen: "Hľadáš inšpiráciu od overených ads." },
  funnel: { description: "VSL (Video Sales Letter) funnel analytika + conversion path.", useWhen: "Pri ladení sales video → trial flow." },
  referral: { description: "Share-and-Earn referral system stats (V162).", useWhen: "Sledovanie referral leaderboard + payouts." },
  social: { description: "Live social proof popups + activity ticker config.", useWhen: "Tuning frequency a copy popupov." },

  // ---- Product ----
  pulseai: { description: "PulseAI core model config + token budgets.", useWhen: "Pri zmene LLM modelu alebo nákladovej optimalizácii." },
  agents: { description: "🤖 14 AI agentov overview: behov, klientov, MRR, fallback %.", useWhen: "Týždenný check: ktorý agent najviac zarába?", popular: true },
  funnels: { description: "🔗 Všetky funnel URLs (30+) — kopíruj/zdieľaj/otvor.", useWhen: "Keď posielaš link do Slacku alebo emailu." },
  roadmap: { description: "🛠️ Kanban roadmap (BACKLOG → SHIPPED) + MRR forecast.", useWhen: "Sprint planning + drag-drop agent na launch.", popular: true },
  gsc_setup: { description: "🔍 Google Search Console — service account ownership guide + verify.", useWhen: "Po deploy: pridať SA email ako Owner pre instant indexing.", badge: "NEW" },
  sitemap_ping: { description: "📡 Denný cron pre Google Indexing API (blog + Local SEO).", useWhen: "Týždenne pozri history; spusti manuálne po veľkom batch publishe.", badge: "NEW" },
  anti_rankpill: { description: "🛡️ Anti-RankPill content theft defense: canaries + DMCA workflow.", useWhen: "Týždenný check, či ti niekto neukradol AI obsah.", badge: "NEW" },
  theme: { description: "Brand theme tokeny + dark/light mode.", useWhen: "Pri rebrandingu alebo úprave farieb." },
  brand: { description: "Brand monitoring: AI viditeľnosť + Google mentions.", useWhen: "Mesačný brand health check." },
  seo: { description: "SEO/GEO scoring engine config.", useWhen: "Pri tuning scan algorithm." },
  rescans: { description: "Plánované opakované scany (PRO feature).", useWhen: "Klient si pýta weekly auto-scan." },
  faqs: { description: "Chat FAQ knowledge base (CRUD).", useWhen: "Keď chatbot odpovedá zle → pridaj FAQ entry." },
  chat_feedback: { description: "Thumbs up/down feedback z chatbota.", useWhen: "Identifikácia weak spots v AI responses." },
  agent_demand: { description: "Vote-for-agent waitlist signups per agent.", useWhen: "Demand-validation pre ďalšie buildy." },

  // ---- Ops ----
  users: { description: "Všetci registrovaní useri + ich plán.", useWhen: "Hľadáš konkrétny účet." },
  users_mgmt: { description: "Správa účtov: impersonate, plan change, ban, delete.", useWhen: "Klient si píše že má problém — impersonate." },
  pricing: { description: "Plán pricing tiers + FX kurzy (USD/EUR/CZK).", useWhen: "Pri zmene cien plánov." },
  eshop_tiers: { description: "E-shop velikostné tiers + ich SEO/GEO add-ony.", useWhen: "Konfigurácia balíkov pre rôzne velkosti shopov." },
  behavior: { description: "User behavior tracking — kde clickujú, kde drop-out.", useWhen: "Pri UX optimalizácii." },
  invoicing: {
    description: "💼 Faktúry (PDF, Resend, Superfaktúra sync, SLSP bank matcher).",
    useWhen: "Mesačný audit + manuálna faktúra pre B2B klienta.",
    popular: true,
  },
  leads_export: {
    description: "📊 Background CSV export celej leads kolekcie (100k+ riadkov).",
    useWhen: "Plný export pre BI nástroje alebo CRM import.",
    badge: "NEW",
  },
  seed_production: {
    description: "🌱 Bulk seed všetkých kľúčových collections (ads templates, FAQs, FB ads, roadmap...).",
    useWhen: "Po fresh deploy na production keď DB-čka sú prázdne.",
    badge: "NEW",
  },
  studio_autopilot: {
    description: "🤖 Týždenný rotujúci pick z Ad Studio presetov + email s deep-linkom.",
    useWhen: "Piatkový workflow: 2 kliky z inboxu po PNG na Meta Ads.",
    badge: "NEW",
  },
  mika_ads: {
    description: "🎭 6 photo-realistických AI-generovaných reklám s Mika personou (gpt-image-1).",
    useWhen: "Keď potrebuješ premium hero kreatívu pre Meta Ads — Tripwire, STOP, 90s Timer, Big Money, Coming Soon, Competitor Killer.",
    badge: "NEW",
  },
};

export function getAdminTabMeta(key) {
  return ADMIN_TAB_META[key] || null;
}

export function getPopularTabs() {
  return Object.entries(ADMIN_TAB_META)
    .filter(([, m]) => m.popular)
    .map(([key, m]) => ({ key, ...m }));
}
